import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Updated FrequencyTable: Transposes the pivot so that option values are in the rows and categories in the columns.
function FrequencyTable({ data }) {
  if (!data) return <p>No frequency data available.</p>;

  // Check if every value is an object (and not null)
  const isPivotable = Object.values(data).every(val => typeof val === 'object' && val !== null);

  if (isPivotable) {
    // Get the union of all nested keys (option values) from every category.
    const allOptionKeysSet = new Set();
    Object.values(data).forEach(obj => {
      Object.keys(obj).forEach(key => {
        allOptionKeysSet.add(key);
      });
    });
    const rowKeys = Array.from(allOptionKeysSet);
    // Sort the option keys if numeric, else lexicographically.
    rowKeys.sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });
    
    // The column keys are the top-level keys (categories).
    const colKeys = Object.keys(data);
    colKeys.sort((a, b) => a.localeCompare(b));

    return (
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '8px' }}>Option Value</th>
            {colKeys.map(col => (
              <th key={col} style={{ border: '1px solid #ddd', padding: '8px' }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map(row => (
            <tr key={row}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row}</td>
              {colKeys.map(col => (
                <td key={col} style={{ border: '1px solid #ddd', padding: '8px' }}>
                  {data[col][row] !== undefined ? data[col][row] : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    // Fallback: if not pivotable, show the original table format.
    const rows = Object.entries(data);
    return (
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '8px' }}>Category</th>
            <th style={{ border: '1px solid #ddd', padding: '8px' }}>Frequency</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([category, value]) => (
            <tr key={category}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{category}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                {typeof value === 'object' ? JSON.stringify(value) : value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

function CrosstabTable({ data, variables }) {
  if (!data) return <p>No crosstab data available.</p>;

  // Expecting an array of 2 variables: [rowVariable, colVariable]
  const rowVar = variables && variables.length > 0 ? variables[0] : { code: "RowVar", label: "Row Variable" };
  const colVar = variables && variables.length > 1 ? variables[1] : { code: "ColVar", label: "Column Variable" };

  // rowKeys are the keys of 'data' (the row categories)
  const rowKeys = Object.keys(data);
  if (rowKeys.length === 0) return <p>No data in crosstab.</p>;

  // colKeys are the keys from the first row's object (the column categories)
  const colKeys = Object.keys(data[rowKeys[0]]);

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th style={{ border: '1px solid #ddd', padding: '8px' }} rowSpan={2}>
            Cross-Tab
          </th>
          <th style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={colKeys.length + 1}>
            {rowVar.label}
          </th>
        </tr>
        <tr>
          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Row \ Column</th>
          {colKeys.map(col => (
            <th key={col} style={{ border: '1px solid #ddd', padding: '8px' }}>
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rowKeys.map((row, rowIndex) => (
          <tr key={row}>
            {rowIndex === 0 && (
              <td style={{ border: '1px solid #ddd', padding: '8px' }} rowSpan={rowKeys.length}>
                {colVar.label}
              </td>
            )}
            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row}</td>
            {colKeys.map(col => (
              <td key={col} style={{ border: '1px solid #ddd', padding: '8px' }}>
                {data[row][col]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChatResponse({ entry }) {
  const { query, result, aiResponse, variables, error, options, message } = entry;

  // Determine if result is an array (for multiple analyses) or a single object.
  const isMultiResult = Array.isArray(result);

  let parsedAIResponse;
  try {
    parsedAIResponse = aiResponse ? JSON.parse(aiResponse) : {};
  } catch (e) {
    parsedAIResponse = aiResponse;
  }

  return (
    <div
      style={{
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '10px',
        marginBottom: '10px',
        backgroundColor: '#f9f9f9'
      }}
    >
      <h4>Query:</h4>
      <p>{query}</p>

      {variables && Array.isArray(variables) && variables.length > 0 && (
        <>
          <h4>Mapped Variables:</h4>
          <ul>
            {variables.map((v, i) => (
              <li key={i}>
                {v.code} - {v.label}
              </li>
            ))}
          </ul>
        </>
      )}

      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          <strong>Error:</strong> {error}
          {options && (
            <div>
              <p>Please choose one of the following variables by typing its code in the chat:</p>
              <ul>
                {options.map((opt, idx) => (
                  <li key={idx}>
                    {opt.code} - {opt.label ? opt.label : opt.code}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {message && (
        <div style={{ color: 'green', marginBottom: '10px' }}>
          <strong>Message:</strong> {message}
        </div>
      )}

      <h4>Result:</h4>
      {isMultiResult ? (
        // For multi-result, check if each item contains a 'variables' array.
        result.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '20px' }}>
            <h5>
              {item.variables && Array.isArray(item.variables)
                ? item.variables.map(v => `${v.code} - ${v.label}`).join(', ')
                : `${String(item.variable?.code)} - ${String(item.variable?.label)}`}
            </h5>
            {(() => {
              const analysisData = item.analysis;
              const isCrosstab = analysisData && Object.values(analysisData).every(
                val => typeof val === 'object' && val !== null
              );
              return isCrosstab ? <CrosstabTable data={analysisData} variables={item.variables} /> : <FrequencyTable data={analysisData} />;
            })()}
          </div>
        ))
      ) : (
        // Single result branch
        (() => {
          const analysisData = result && result.analysis ? result.analysis : result;
          const hasAnalysis = analysisData && Object.keys(analysisData).length > 0;
          const isCrosstab = hasAnalysis && Object.values(analysisData).every(
            val => typeof val === 'object' && val !== null
          );
          return hasAnalysis ? (
            isCrosstab ? <CrosstabTable data={analysisData} /> : <FrequencyTable data={analysisData} />
          ) : (
            <p>No analysis result available.</p>
          );
        })()
      )}

      <h4>AI Response:</h4>
      <pre style={{ whiteSpace: 'pre-wrap' }}>
        {parsedAIResponse
          ? JSON.stringify(parsedAIResponse, null, 2)
          : 'No AI response available.'}
      </pre>
    </div>
  );
}

function Chatbot() {
  const [responses, setResponses] = useState([]);
  const [query, setQuery] = useState('');
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pendingOptions, setPendingOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    api
      .get('drivers/list_files/', {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` }
      })
      .then(response => {
        setFiles(response.data);
      })
      .catch(error => {
        console.error('Error fetching files', error);
      });
  }, []);

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const submitQuery = (chosenColumn = null) => {
    setIsLoading(true);
    const payload = { user_input: query, file_id: selectedFile ? selectedFile.id : null };
    if (chosenColumn) {
      payload.chosen_column = chosenColumn;
    }
    api
      .post('chatbot/chatbot/', payload, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` }
      })
      .then(response => {
        if (response.data.options) {
          setPendingOptions(response.data.options);
          const errorEntry = {
            query,
            error: response.data.message,
            options: response.data.options,
            aiResponse: response.data.ai_response,
            variables: response.data.variables || []
          };
          setResponses(prev => [errorEntry, ...prev]);
        } else {
          const newEntry = {
            query,
            result: response.data.result,
            aiResponse: response.data.ai_response,
            variables: response.data.variables,
            message: response.data.message
          };
          setResponses(prev => [newEntry, ...prev]);
          setPendingOptions(null);
        }
        setQuery('');
        setIsLoading(false);
      })
      .catch(error => {
        console.error('There was an error!', error);
        setIsLoading(false);
      });
  };

  const handleSubmit = () => {
    if (pendingOptions && pendingOptions.some(opt => opt.code === query.trim())) {
      submitQuery(query.trim());
      setPendingOptions(null);
    } else {
      submitQuery();
    }
  };

  const handleFileSelection = (file) => {
    setSelectedFile(file);
  };

  return (
    <div style={{ paddingTop: '10px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3>Uploaded Files</h3>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          {files.map(file => (
            <li key={file.id} style={{ marginBottom: '5px' }}>
              <button 
                onClick={() => handleFileSelection(file)}
                style={{
                  background: selectedFile && selectedFile.id === file.id ? 'lightblue' : 'white',
                  padding: '5px 10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {file.name}
              </button>
            </li>
          ))}
        </ul>
        {selectedFile && (
          <p>Selected File: <strong>{selectedFile.name}</strong></p>
        )}
      </div>
      <div style={{ marginBottom: '20px' }}>
        {isLoading ? <p>Loading...</p> : responses.map((entry, index) => (
          <ChatResponse key={index} entry={entry} />
        ))}
      </div>
      <div style={{ marginLeft: '20px', paddingTop: '5px' }}>
        <input
          type="text"
          id="textInput"
          value={query}
          onChange={handleQueryChange}
          placeholder="Type your query or variable code..."
          style={{ width: '74%', height: '30px', marginBottom: '10px', borderRadius: '5px', padding: '5px' }}
        />
        <button onClick={handleSubmit} style={{ height: '36px', marginLeft: '10px' }}>
          Submit
        </button>
      </div>
    </div>
  );
}

export default Chatbot;
